"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/symptoms-exam.module.css";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";

export default function SymptomsExam() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const [saving, setSaving] = useState(false);
  
  const draftKey = "symptomsExam";

  const defaultFormData = {
    // Main Complaint
    mainComplaint: "",
    selectedComplaints: [],
    
    // Symptoms Reported
    symptomsReported: "",
    
    // Physical Examination Findings
    examinationFindings: "",
    
    // Question Asked / Answer Provided (array of Q&A pairs)
    qaPairs: [{ question: "", answer: "" }]
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  // Preset complaint options
  const complaintOptions = [
    "Fever",
    "Headache", 
    "Abdominal pain",
    "Cough",
    "Sore throat",
    "Back pain",
    "Chest pain",
    "Dizziness",
    "Nausea",
    "Diarrhea",
    "Skin rash",
    "Joint pain",
    "Fatigue",
    "Shortness of breath",
    "Contraception review",
    "Chronic medication review",
    "Follow-up visit"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const addQuestion = () => {
    setFormData((prev) => {
      const next = { ...prev, qaPairs: [...prev.qaPairs, { question: "", answer: "" }] };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handleQAChange = (index, field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        qaPairs: prev.qaPairs.map((pair, i) =>
          i === index ? { ...pair, [field]: value } : pair
        ),
      };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handleComplaintToggle = (complaint) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        selectedComplaints: prev.selectedComplaints.includes(complaint)
          ? prev.selectedComplaints.filter((c) => c !== complaint)
          : [...prev.selectedComplaints, complaint],
      };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/vitals-observations");
  };

  const handleNext = async () => {
    try {
      setSaving(true);
      nextStep();
      navigate("/customer/clinic/specialized-tests");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = consultationData?.consultationId;
        if (!id) return;
        const full = await getConsultationFull(id);
        if (cancelled || !full) return;

        const c = full.consultation_complaint;
        const tags = Array.isArray(full.consultation_complaint_tag) ? full.consultation_complaint_tag : [];
        const qa = Array.isArray(full.consultation_qa) ? full.consultation_qa : [];

        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const next = {
          ...defaultFormData,
          mainComplaint: c?.maincomplaint ?? "",
          symptomsReported: c?.symptomsreported ?? "",
          examinationFindings: c?.examinationfindings ?? "",
          selectedComplaints: tags.map((t) => t.tag).filter(Boolean),
          qaPairs: qa.length
            ? qa
                .slice()
                .sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
                .map((x) => ({ question: x.question || "", answer: x.answer || "" }))
            : defaultFormData.qaPairs,
        };

        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={4}
            totalSteps={10}
            stepTitle="Main Complaint"
            stepDetails="Step 4 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* MAIN COMPLAINT */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>MAIN COMPLAINT</h3>
              <p className={styles.sectionSubtitle}>What is the patient's primary complaint?</p>
              
              {/* Preset complaint buttons */}
              <div className={styles.complaintButtonsGrid}>
                {complaintOptions.map((complaint) => (
                  <button
                    key={complaint}
                    type="button"
                    className={`${styles.complaintButton} ${
                      formData.selectedComplaints.includes(complaint) ? styles.active : ""
                    }`}
                    onClick={() => handleComplaintToggle(complaint)}
                  >
                    {complaint}
                  </button>
                ))}
              </div>
              
              {/* Free text description */}
              <div className={styles.formGroup}>
                <label>Describe the patient's primary complaint...</label>
                <textarea 
                  name="mainComplaint"
                  value={formData.mainComplaint}
                  onChange={handleInputChange}
                  placeholder="Provide detailed description of the primary complaint..."
                  className={styles.fullWidthTextarea}
                />
              </div>
            </div>

            {/* SYMPTOMS REPORTED + PHYSICAL EXAMINATION FINDINGS (Same Row) */}
            <div className={styles.twoRowLayout}>
              {/* SYMPTOMS REPORTED */}
              <div className={styles.sectionCard}>
                <h3 className={styles.blueSubHeader}>SYMPTOMS REPORTED</h3>
                <div className={styles.formGroup}>
                  <textarea 
                    name="symptomsReported"
                    value={formData.symptomsReported}
                    onChange={handleInputChange}
                    placeholder="List symptoms in the patient's own words..."
                    className={styles.fullWidthTextarea}
                  />
                </div>
              </div>

              {/* PHYSICAL EXAMINATION FINDINGS */}
              <div className={styles.sectionCard}>
                <h3 className={styles.blueSubHeader}>PHYSICAL EXAMINATION FINDINGS</h3>
                <div className={styles.formGroup}>
                  <textarea 
                    name="examinationFindings"
                    value={formData.examinationFindings}
                    onChange={handleInputChange}
                    placeholder="Record objective clinical observations (e.g., vital signs, abnormal findings)..."
                    className={styles.fullWidthTextarea}
                  />
                </div>
              </div>
            </div>

            {/* QUESTION ASKED / ANSWER PROVIDED */}
            <div className={styles.sectionCard}>
              {formData.qaPairs.map((pair, index) => (
                <div key={index} className={styles.qaPair}>
                  <div className={styles.questionRow}>
                    <div className={styles.questionInputWrapper}>
                      <label className={styles.blueLabel}>QUESTION ASKED</label>
                      <input
                        type="text"
                        value={pair.question}
                        onChange={(e) => handleQAChange(index, "question", e.target.value)}
                        placeholder=""
                        className={styles.questionInput}
                      />
                    </div>
                    {index === formData.qaPairs.length - 1 && (
                      <button
                        type="button"
                        className={styles.addQuestionButton}
                        onClick={addQuestion}
                      >
                        + Question
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.answerWrapper}>
                    <label className={styles.blueLabel}>ANSWER PROVIDED BY PATIENT</label>
                    <textarea
                      value={pair.answer}
                      onChange={(e) => handleQAChange(index, "answer", e.target.value)}
                      placeholder=""
                      className={styles.answerTextarea}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
